import React, { forwardRef, useCallback, useRef } from 'react';
import globalStyles from './internal/global.styles';
import fonts from './internal/fonts.styles';
import { ThemeTypes } from '@polestar/component-warehouse-react/themes/Theme';
import Themes from '@polestar/component-warehouse-react/themes/Themes';
import { Global } from '@emotion/react';
import styled from '@emotion/styled';
import version from '@polestar/component-warehouse-react/version';
import { ContextsProviders } from '@polestar/component-warehouse-react/contexts/appContexts';
import { useModal } from '@polestar/component-warehouse-react/contexts/modal';
import { useDirection } from '@polestar/component-warehouse-react/contexts/direction';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import { useContentElement } from '@polestar/component-warehouse-react/contexts/contentElement';
// This is outside of Global because that will unmount when switching between
// SSR generated stylesheet and JS generated one. Which causes a double FONT.
const fontFaces = ({ domainOverride, newFont } = {}) => (React.createElement("style", { key: "global-fonts", type: "text/css", 
    // @ts-ignore
    dangerouslySetInnerHTML: { __html: fonts({ domainOverride, newFont }) } }));
const PolestarApp = forwardRef(({ rootSelector = 'body', theme = ThemeTypes.light, themes = Themes, instantThemeTransition = false, initialDirection, config, ...props }, ref) => {
    const currentThemes = themes === Themes ? themes : { ...Themes, ...themes };
    const currentTheme = (typeof theme === 'string' ? themes[theme] : theme);
    return (React.createElement(ContextsProviders, { theme: currentTheme, themes: currentThemes, initialDirection: initialDirection, config: config },
        React.createElement(Global, { styles: globalStyles({
                rootSelector,
                theme: currentTheme,
                instantThemeTransition,
            }) }),
        React.createElement(Content, { ref: ref, ...props, "data-version": version, ...((config === null || config === void 0 ? void 0 : config.newFont) ? { 'data-font': '' } : {}) }),
        fontFaces(config)));
});
PolestarApp.displayName = 'PolestarApp';
export default PolestarApp;
const Content = forwardRef((props, ref) => {
    const { direction } = useDirection();
    const { contentRef } = useContentElement();
    const bodyScroll = useRef(0);
    const combinedRefs = useCombinedRef(ref, contentRef);
    const handleAria = useCallback((openModals, { applyInputFix }) => {
        var _a;
        const hasOpenModals = openModals.length > 0;
        (_a = contentRef === null || contentRef === void 0 ? void 0 : contentRef.current) === null || _a === void 0 ? void 0 : _a.setAttribute('aria-hidden', hasOpenModals ? 'true' : 'false');
        /**
         * Safari on iOs tries to apply some magic to get the input in the viewport. This breaks the modal.
         * An example of this is when you open the autocomplete field, the input is pressed of the screen by the keyboard.
         * We apply both a position fixed on the page and the modal. When the modal closes we remove it.
         */
        if (hasOpenModals && applyInputFix) {
            /**
             * When we apply the position fixed, the scroll position resets to 0. To solve that we keep
             * track of the scroll position in order to restore it when the modal closes.
             */
            bodyScroll.current = document.documentElement.scrollTop;
            document.body.classList.add('cw-scrolling-input-fix');
        }
        else {
            document.body.classList.remove('cw-scrolling-input-fix');
            if (bodyScroll.current !== 0) {
                document.documentElement.scrollTop = bodyScroll.current;
                bodyScroll.current = 0;
            }
        }
        if (hasOpenModals) {
            document.body.classList.add('cw-disabled-scrolling');
        }
        else {
            document.body.classList.remove('cw-disabled-scrolling');
        }
    }, [contentRef]);
    useModal({
        onModalsChange: handleAria,
    });
    return React.createElement(Container, { ref: combinedRefs, ...props, dir: direction });
});
Content.displayName = 'Content';
const Container = styled.div `
  display: flex;
  flex-direction: column;
  margin: 0 auto;
  min-height: 100%;
`;
/**
 * <main> is (or became) a standard https://html.spec.whatwg.org/multipage/grouping-content.html#the-main-element but Typescript
 * did not agree : https://github.com/microsoft/TypeScript/issues/32042 / https://github.com/microsoft/TypeScript-DOM-lib-generator/pull/655
 */
export const PageContent = forwardRef((props, ref) => {
    return React.createElement(PageContentContainer, { ref: ref, ...props });
});
PageContent.displayName = 'PageContent';
const PageContentContainer = styled.main `
  flex: 1;
`;
